/**
 * Punch-sequence anomalies a staffer can fix themselves. A day's punches should
 * alternate in → out → in → out; two in a row (e.g. an accidental second
 * clock-in) is a "double". The SECOND punch of a double is the one out of
 * sequence, so it's the switch candidate — the same rule the backend enforces.
 */

import { TimeEntryResponse } from '@/types/api';

export interface DoublePunch {
  /** yyyy-MM-dd */
  date: string;
  /** Both punches are clock-ins ('in') or both clock-outs ('out'). */
  direction: 'in' | 'out';
  first: TimeEntryResponse;
  /** The out-of-sequence punch — switching it fixes the pair. */
  second: TimeEntryResponse;
}

const timeOf = (e: TimeEntryResponse) => e.adjustedTime || e.actualTime || '';

/** Doubles in each day's punches (ignored punches skipped), in time order. */
export const findDoublePunches = (entries: TimeEntryResponse[]): DoublePunch[] => {
  const byDay = new Map<string, TimeEntryResponse[]>();
  for (const e of entries) {
    if (e.ignored || !timeOf(e)) continue;
    const list = byDay.get(e.punchDate) ?? [];
    list.push(e);
    byDay.set(e.punchDate, list);
  }
  const out: DoublePunch[] = [];
  for (const [date, list] of byDay) {
    list.sort((a, b) => timeOf(a).localeCompare(timeOf(b)));
    for (let i = 1; i < list.length; i++) {
      if (list[i].clockIn === list[i - 1].clockIn) {
        out.push({
          date,
          direction: list[i].clockIn ? 'in' : 'out',
          first: list[i - 1],
          second: list[i],
        });
      }
    }
  }
  return out.sort((a, b) => b.date.localeCompare(a.date) || timeOf(a.second).localeCompare(timeOf(b.second)));
};

/** Ids of every punch that's part of a double (to hide redundant per-row fixes). */
export const doublePunchIds = (doubles: DoublePunch[]): Set<number> => {
  const ids = new Set<number>();
  for (const d of doubles) {
    ids.add(d.first.id);
    ids.add(d.second.id);
  }
  return ids;
};
